import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type {
    AuthState,
    LoginCredentials,
    MFAVerificationData,
    MfaSetupResponse,
    JWTClaims,
} from '../types/auth.types';
import { login as submitLogin, refresh } from '../services/auth';
import { mfa_setup, mfa_send, mfa_verify, type MfaSetupRequest } from '../services/mfa';
import { fetchUserRbac } from '../services/rbac';
import { verifyJwt } from '../utils/verifyJwt';
import { tokenRefreshManager } from '../utils/tokenRefreshManager';
import { isApiError } from '@/services/api';
import { clearIncludeThirdParty } from '@features/dashboard/utils/includeThirdPartyStorage';

// Move your existing thunks here and enhance them
export const login = createAsyncThunk(
    'auth/login',
    async (credentials: LoginCredentials, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { device: { uuid: string | null } };

            if (!state.device.uuid) {
                throw new Error('Device UUID not initialized');
            }

            const loginRequest = {
                email: credentials.email,
                password: credentials.password,
                device_uuid: state.device.uuid,
                remember_me: credentials.remember_me || false,
            };

            // Use real API call (comment out the mock for production)
            const result = await submitLogin(loginRequest);

            if (!result.token) {
                throw new Error('No token received from server');
            }

            // Add timeout handling for JWT verification
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('JWT verification timeout')), 5000);
            });

            const jwtPayload = (await Promise.race([verifyJwt(result.token), timeoutPromise])) as {
                payload: JWTClaims;
            } | null;

            if (!jwtPayload) {
                throw new Error('Failed to verify JWT token');
            }

            clearIncludeThirdParty();

            const claims = jwtPayload.payload as JWTClaims;

            return {
                token: result.token,
                user: result.user,
                remember_me: result.remember_me,
                mfa_status: claims.mfa_status,
                mfa_method: claims.mfa_method,
                dealer_id: claims.dealer_id,
                is_internal: claims.is_internal === true,
            };
        } catch (error) {
            if (isApiError(error)) {
                return rejectWithValue(error.message);
            }
            return rejectWithValue(error instanceof Error ? error.message : 'Login failed');
        }
    }
);

export const sendMFA = createAsyncThunk('auth/sendMFA', async (_, { rejectWithValue }) => {
    try {
        const result = await mfa_send();
        return {
            method: result.method,
        };
    } catch (error) {
        if (isApiError(error)) {
            return rejectWithValue(error.message);
        }
        return rejectWithValue(error instanceof Error ? error.message : 'Failed to send MFA code');
    }
});

export const setupMFA = createAsyncThunk(
    'auth/setupMFA',
    async (mfaSetup: MfaSetupRequest, { rejectWithValue }) => {
        try {
            const response: MfaSetupResponse = await mfa_setup(mfaSetup);

            return {
                qr_code: response.qr_code,
                secret: response.secret,
                method: response.method,
                message: response.message || 'MFA setup successful',
            };
        } catch (error) {
            if (isApiError(error)) {
                return rejectWithValue(error.message);
            }
            return rejectWithValue(error instanceof Error ? error.message : 'MFA setup failed');
        }
    }
);

export const verifyMFA = createAsyncThunk(
    'auth/verifyMFA',
    async (data: MFAVerificationData, { getState, rejectWithValue }) => {
        const state = getState() as { auth: AuthState; device: { uuid: string | null } };

        try {
            if (!data.code) {
                throw new Error('MFA code is required');
            }

            const method = data.method ?? state.auth.mfa.method ?? '';
            const isRecoveryCode = method === 'recovery_code';

            const result = await mfa_verify({
                token: data.code,
                method,
                device_uuid: isRecoveryCode ? undefined : (state.device.uuid || undefined),
                remember_device: isRecoveryCode ? false : (data.remember_device || false),
            });

            if (!result.verified) {
                throw new Error('MFA verification failed');
            }

            return { verified: true, recovery_codes: result.recovery_codes };
        } catch (error) {
            if (isApiError(error)) {
                return rejectWithValue(error.message);
            }
            return rejectWithValue(
                error instanceof Error ? error.message : 'MFA verification failed'
            );
        }
    }
);

export const refreshToken = createAsyncThunk(
    'auth/refreshToken',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { auth: AuthState; device: { uuid: string | null } };

            // state.auth.user may be null on cold boot — the startup refresh in
            // AuthProvider runs before any login. Only the device UUID is required
            // because /api/auth/refresh validates it alongside the HttpOnly cookie.
            if (!state.device.uuid) {
                throw new Error('Device UUID not initialized');
            }

            const result = await refresh();

            if (!result.token) {
                throw new Error('No token received from server');
            }

            // Add timeout handling for JWT verification
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('JWT verification timeout')), 5000);
            });

            const jwtPayload = (await Promise.race([verifyJwt(result.token), timeoutPromise])) as {
                payload: JWTClaims;
            } | null;

            if (!jwtPayload) {
                throw new Error('Failed to verify JWT token');
            }

            const claims = jwtPayload.payload as JWTClaims;

            return {
                token: result.token,
                user: result.user,
                remember_me: result.remember_me,
                mfa_status: claims.mfa_status,
                mfa_method: claims.mfa_method,
                dealer_id: claims.dealer_id,
                is_internal: claims.is_internal === true,
            };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Token refresh failed');
        }
    }
);

export const getUserRBAC = createAsyncThunk(
    'auth/getUserRBAC',
    async (_, { rejectWithValue }) => {
        try {
            const userInfo = await fetchUserRbac();
            return userInfo;
        } catch (error) {
            return rejectWithValue(
                error instanceof Error ? error.message : 'Failed to get user info'
            );
        }
    }
);

const initialState: AuthState = {
    token: null,
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
    mfa: {
        status: null,
        method: null,
    },
    remember_me: false,
    rbacStatus: 'idle',
    rbacError: null,
    dealerId: null,
    isInternal: false,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            // Stop the refresh scheduler on logout
            tokenRefreshManager.stopRefreshScheduler();

            state.token = null;
            state.user = null;
            state.isAuthenticated = false;
            state.mfa.status = null;
            state.mfa.method = null;
            state.mfa.totpQrCode = undefined;
            state.mfa.totpSecret = undefined;
            state.mfa.method = null;
            state.remember_me = false;
            state.error = null;
            state.rbacStatus = 'idle';
            state.rbacError = null;
            state.dealerId = null;
            state.isInternal = false;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetRbacStatus: (state) => {
            state.rbacStatus = 'idle';
            state.rbacError = null;
        },
        updateUser: (state, action) => {
            if (state.user) {
                state.user = { ...state.user, ...action.payload };
            }
        },
        startTokenRefreshScheduler: (state) => {
            if (state.token && state.isAuthenticated) {
                // Start the refresh scheduler with the current token
                tokenRefreshManager.startRefreshScheduler(state.token, {
                    refreshThresholdMinutes: 5, // Refresh 5 minutes before expiry
                    onRefresh: async () => {
                        // This will be handled by middleware or component
                        // We'll dispatch the automaticRefreshToken action
                    },
                    onError: (error) => {
                        console.error('Token refresh error:', error);
                    },
                });
            }
        },
        stopTokenRefreshScheduler: () => {
            tokenRefreshManager.stopRefreshScheduler();
        },
    },
    extraReducers: (builder) => {
        builder
            // Login
            .addCase(login.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.isLoading = false;
                state.token = action.payload.token;
                state.user = action.payload.user!;
                state.remember_me = action.payload.remember_me;
                state.isAuthenticated =
                    action.payload.mfa_status === 'setup_required' ||
                    action.payload.mfa_status === 'required'
                        ? false
                        : true;
                state.mfa = {
                    status: action.payload.mfa_status,
                    method: action.payload.mfa_method,
                };
                state.dealerId = action.payload.dealer_id ?? null;
                state.isInternal = action.payload.is_internal;

                // Start token refresh scheduler if user is fully authenticated
                if (state.isAuthenticated && state.token) {
                    tokenRefreshManager.startRefreshScheduler(state.token, {
                        refreshThresholdMinutes: 5,
                        onRefresh: async () => {
                            // This will trigger automatic refresh
                            // The refresh will be handled by middleware or component
                        },
                        onError: (error) => {
                            console.error('Automatic token refresh failed:', error);
                        },
                    });
                }

                // Mark RBAC as pending so ProtectedRoutes shows AppLoader instead
                // of evaluating PermissionRoute against empty roles. Skip for MFA
                // flows — AuthRoutes redirects to /mfa/verify and RBAC is irrelevant.
                if (state.isAuthenticated) {
                    state.rbacStatus = 'loading';
                    state.rbacError = null;
                }
            })
            .addCase(login.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // MFA Interim Send
            .addCase(sendMFA.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(sendMFA.fulfilled, (state, action) => {
                state.isLoading = false;
                state.mfa = {
                    status: state.mfa.status || 'required',
                    method: action.payload.method,
                };
            })
            .addCase(sendMFA.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // MFA Setup
            .addCase(setupMFA.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(setupMFA.fulfilled, (state, action) => {
                state.isLoading = false;
                state.mfa = {
                    status: state.mfa.status || 'setup_required',
                    method: action.payload.method,
                    totpQrCode: action.payload.qr_code || undefined,
                    totpSecret: action.payload.secret || undefined,
                };
            })
            .addCase(setupMFA.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // MFA Verification
            .addCase(verifyMFA.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(verifyMFA.fulfilled, (state) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.mfa = {
                    status: 'verified',
                    method: null,
                };

                // Start token refresh scheduler after MFA verification
                if (state.token) {
                    tokenRefreshManager.startRefreshScheduler(state.token, {
                        refreshThresholdMinutes: 5,
                        onRefresh: async () => {
                            // This will trigger automatic refresh
                        },
                        onError: (error) => {
                            console.error('Automatic token refresh failed:', error);
                        },
                    });
                }

                state.rbacStatus = 'loading';
                state.rbacError = null;
            })
            .addCase(verifyMFA.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Do Refresh Token
            .addCase(refreshToken.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(refreshToken.fulfilled, (state, action) => {
                state.isLoading = false;
                state.token = action.payload.token;
                const existingRoles = state.user?.roles ?? [];
                const existingPermissions = state.user?.permissions ?? [];
                state.user = action.payload.user!;
                if (state.user) {
                    state.user.roles = existingRoles;
                    state.user.permissions = existingPermissions;
                }
                state.remember_me = action.payload.remember_me;
                state.isAuthenticated =
                    action.payload.mfa_status === 'setup_required' ||
                    action.payload.mfa_status === 'required'
                        ? false
                        : true;
                state.mfa = {
                    status: action.payload.mfa_status,
                    method: action.payload.mfa_method,
                };
                state.dealerId = action.payload.dealer_id ?? null;
                state.isInternal = action.payload.is_internal;

                // Update the refresh manager with the new token
                if (state.token) {
                    tokenRefreshManager.updateToken(state.token);
                }

                // The cold-boot path and useTokenRefresh chain both follow
                // refreshToken with getUserRBAC. Marking 'loading' keeps the
                // field consistent across all entry points; existing roles
                // remain in state so ProtectedRoutes does not flash the loader
                // during a silent mid-session refetch.
                if (state.isAuthenticated) {
                    state.rbacStatus = 'loading';
                    state.rbacError = null;
                }
            })
            .addCase(refreshToken.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Get roles
            .addCase(getUserRBAC.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.rbacStatus = 'loading';
                state.rbacError = null;
            })
            .addCase(getUserRBAC.fulfilled, (state, action) => {
                state.isLoading = false;
                if (state.user) {
                    state.user.roles = action.payload.roles;
                    state.user.permissions = action.payload.permissions;
                }
                state.rbacStatus = 'ready';
                state.rbacError = null;
            })
            .addCase(getUserRBAC.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
                state.rbacStatus = 'error';
                state.rbacError = action.payload as string;
            });
    },
});

export const {
    logout,
    clearError,
    updateUser,
    startTokenRefreshScheduler,
    stopTokenRefreshScheduler,
    resetRbacStatus,
} = authSlice.actions;
export default authSlice.reducer;
